/**
 * R1 — Sync accepted-connection peers (with phone numbers) into
 * Capacitor Preferences key `elefa.car.contacts` for the in-car
 * Contacts tab on Android Auto / CarPlay.
 *
 * Single writer, two readers:
 *   - Android: tel.elefa.car.ElefaCarAppService → ContactsScreen
 *     reads SharedPreferences("CapacitorStorage").getString("elefa.car.contacts")
 *   - iOS:     ElefaCarPlaySceneDelegate (post-entitlement)
 *
 * Data source: existing `connection_requests` table where status='accepted'
 * (the same accepted-peer set the chat layer already uses). Profile cards —
 * name + phone_number — fetched via the SECURITY DEFINER RPC
 * `get_connection_profile_cards` (already used by useConnectionRequests, so
 * no new RLS surface area, no new edge function, no schema change).
 *
 * Guardrail: no Supabase migration, no FreeSWITCH/Kamailio touch — pure
 * read of an existing table + existing RPC, written to a local Preferences
 * cache. Conforms to scripts/car-design/INFRA-MAP.md.
 *
 * Privacy: stores ONLY (name, e164). Cap of 100 entries to keep the
 * Preferences blob small and the car ListTemplate paginate-able.
 */
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { supabase } from "@/integrations/supabase/client";

const CAR_CONTACTS_KEY = "elefa.car.contacts";
const MAX_ENTRIES = 100;
// Refresh cadence — accepted-connection set changes rarely. Keep light.
const REFRESH_MS = 5 * 60 * 1000;

interface CarContact {
  name: string;
  e164: string;
}

function toE164(raw: string | null | undefined): string {
  if (!raw) return "";
  const cleaned = raw.replace(/[^\d+]/g, "");
  if (/^\+\d{6,15}$/.test(cleaned)) return cleaned;
  if (/^\d{6,15}$/.test(cleaned)) return `+${cleaned}`;
  return "";
}

async function buildContacts(userId: string): Promise<CarContact[]> {
  // 1. Accepted-connection peer ids (both directions).
  const { data: rows, error } = await supabase
    .from("connection_requests")
    .select("sender_id,receiver_id")
    .eq("status", "accepted")
    .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`);
  if (error || !rows?.length) return [];

  const peerIds = Array.from(
    new Set(
      rows.map((r: { sender_id: string; receiver_id: string }) =>
        r.sender_id === userId ? r.receiver_id : r.sender_id,
      ),
    ),
  );
  if (!peerIds.length) return [];

  // 2. Resolve names + phone numbers via the same RPC used by chat.
  const { data: cards } = await supabase.rpc("get_connection_profile_cards", {
    p_user_ids: peerIds,
  });
  if (!Array.isArray(cards)) return [];

  const out: CarContact[] = [];
  const seen = new Set<string>();
  for (const c of cards as Array<{
    full_name: string | null;
    phone_number: string | null;
  }>) {
    const e164 = toE164(c.phone_number);
    if (!e164 || seen.has(e164)) continue;
    seen.add(e164);
    const name = (c.full_name && c.full_name.trim()) || e164;
    out.push({ name, e164 });
    if (out.length >= MAX_ENTRIES) break;
  }
  // A–Z sort matches the native ContactsScreen grouping.
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

export default function useCarContactsSync() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const id = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const uid = data.user?.id;
        if (!uid) return;
        const contacts = await buildContacts(uid);
        if (cancelled) return;
        await Preferences.set({
          key: CAR_CONTACTS_KEY,
          value: JSON.stringify(contacts),
        });
      } catch {
        /* non-fatal — car UI shows empty state */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);
}
